import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { DashboardAlerts } from '../components/DashboardAlerts';
import { OverdueDebtsWidget } from '../components/OverdueDebtsWidget';
import { ExpenseDueAlertsWidget } from '../components/ExpenseDueAlertsWidget';
import { TopDebtorsWidget } from '../components/TopDebtorsWidget';
import { CustomerStatementModal } from '../components/CustomerStatementModal';
import { TopDeliveryCustomers } from '../components/TopDeliveryCustomers';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Package, AlertCircle, Award, ShoppingBag, RotateCcw,
  AlertTriangle, Percent, Zap, DollarSign, Target, Sparkles, Layers, BarChart3,
} from 'lucide-react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import { overlayClose } from '../lib/overlayClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Paletas: dark (padrão corporativo) e light (tema claro)
const DARK = {
  bg: '#0a0f1e',
  card: '#0f1629',
  cardHover: '#132040',
  border: 'rgba(56, 189, 248, 0.28)',
  emerald: '#10B981',
  amber: '#F59E0B',
  gold: '#EAB308',
  brown: '#451A03',
  coral: '#F87171',
  blue: '#1D4ED8',
  violet: '#A78BFA',
  purple: '#4C1D95',
  cyan: '#22D3EE',
  slate: '#94A3B8',
  slateLight: '#CBD5E1',
  textStrong: '#FFFFFF',
  textMuted: '#94A3B8',
  chartGrid: 'rgba(56, 189, 248, 0.12)',
  tooltipBg: '#0f1629',
  chipBg: '#0a0f1e',
};
const LIGHT = {
  bg: '#F1F5F9',
  card: '#FFFFFF',
  cardHover: '#F8FAFC',
  border: 'rgba(15, 23, 42, 0.08)',
  emerald: '#059669',
  amber: '#D97706',
  gold: '#CA8A04',
  brown: '#78350F',
  coral: '#DC2626',
  blue: '#1D4ED8',
  violet: '#7C3AED',
  purple: '#6D28D9',
  cyan: '#0891B2',
  slate: '#64748B',
  slateLight: '#334155',
  textStrong: '#0F172A',
  textMuted: '#475569',
  chartGrid: 'rgba(15, 23, 42, 0.08)',
  tooltipBg: '#FFFFFF',
  chipBg: '#F1F5F9',
};

const CATEGORY_PIE_COLORS = ['#10B981', '#22D3EE', '#F59E0B', '#F87171', '#A78BFA', '#EAB308', '#F472B6', '#60A5FA'];
const CATEGORY_LABELS = { acougue: 'Açougue', bebidas: 'Bebidas', mercearia: 'Mercearia', outros: 'Outros', flv: 'FLV', padaria: 'Padaria', frios_laticinios: 'Frios', limpeza: 'Limpeza', higiene: 'Higiene' };

// Mapa de cores por nome de categoria (após tradução).
// Açougue = VERMELHO VIVO (destaque solicitado). Outras seguem paleta padrão.
const CATEGORY_COLOR_BY_NAME = {
  'Açougue': '#DC2626',        // vermelho vivo
  'Mercearia': '#F59E0B',      // âmbar
  'Bebidas': '#22D3EE',        // ciano
  'FLV': '#10B981',            // verde
  'Frios': '#60A5FA',          // azul claro
  'Padaria': '#EAB308',        // amarelo
  'Limpeza': '#A78BFA',        // violeta
  'Higiene': '#D946EF',        // fúcsia
  'Outros': '#94A3B8',         // cinza
};
const colorForCategory = (name, idx) => CATEGORY_COLOR_BY_NAME[name] || CATEGORY_PIE_COLORS[idx % CATEGORY_PIE_COLORS.length];

const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const fmtBRL = (n) => `R$ ${Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (n) => Number(n || 0).toLocaleString('pt-BR');

// -------- Simulação (dados fictícios saudáveis p/ apresentação) --------
const SIMULATION_DATA = {
  todaySales: 3847.90,
  todayCount: 47,
  yesterdaySales: 3210.50,
  monthSales: 68429.80,
  yearSales: 542180.30,
  yearGoal: 720000,
  monthProfit: 18320.45,
  monthRevenue: 68429.80,
  discountTimeline: Array.from({ length: 14 }, (_, i) => ({
    label: `${28 - 13 + i}/07`,
    discount: 60 + Math.random() * 180,
    percent: 1.2 + Math.random() * 2.4,
  })),
  categoryPie: [
    { name: 'Açougue', value: 28540 },
    { name: 'Mercearia', value: 19870 },
    { name: 'Bebidas', value: 12420 },
    { name: 'FLV', value: 4890 },
    { name: 'Frios', value: 2710 },
  ],
  topProducts: [
    { name: 'Picanha bovina', total_quantity: 342, total_revenue: 28460 },
    { name: 'Coca-Cola 2L', total_quantity: 1120, total_revenue: 14280 },
    { name: 'Arroz 5kg', total_quantity: 458, total_revenue: 11380 },
    { name: 'Coxão mole', total_quantity: 214, total_revenue: 9240 },
  ],
  lowStockCount: 3,
  staleCount: 8,
  capitalLocked: 12480.60,
  reversalsCount: 2,
  reversalsTotal: 187.30,
  overdueCount: 4,
  overdueTotal: 1234.50,
};

export const DashboardPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const COLORS = theme === 'claro' ? LIGHT : DARK;
  const isLight = theme === 'claro';
  const [comparison, setComparison] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [salesByCategory, setSalesByCategory] = useState(null);
  const [todayReversals, setTodayReversals] = useState({ count: 0, total: 0 });
  const [overdue, setOverdue] = useState({ count: 0, total: 0, total_with_interest: 0 });
  const [statementCustomerId, setStatementCustomerId] = useState(null);
  const [monthProfit, setMonthProfit] = useState(0);
  const [monthRevenue, setMonthRevenue] = useState(0);
  const [discountAnalytics, setDiscountAnalytics] = useState(null);
  const [todayStats, setTodayStats] = useState({ count: 0, total: 0 });
  const [yesterdayStats, setYesterdayStats] = useState({ count: 0, total: 0 });
  const [inventory, setInventory] = useState({ low_stock_count: 0, stale_count: 0, capital_locked: 0, capital_top: [], stale: [], overstock: [] });
  const [showCapitalDiag, setShowCapitalDiag] = useState(false);
  const [loading, setLoading] = useState(true);
  const [simulation, setSimulation] = useState(false);

  const now = useMemo(() => new Date(), []);

  useEffect(() => {
    const loadAll = async () => {
      try {
        const yyyy = now.getFullYear();
        const mm = now.getMonth() + 1;
        const y = new Date(now); y.setDate(y.getDate() - 1);
        const yStart = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 0, 0, 0).toISOString();
        const yEnd = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 59).toISOString();

        const [salesRes, compRes, topRes, catRes, cancelledRes, overdueRes, dailyRes, discRes, invRes, ySalesRes] = await Promise.all([
          axios.get(`${API}/sales?today_only=true&limit=1000`, { withCredentials: true }),
          axios.get(`${API}/dashboard/yearly-comparison`, { withCredentials: true }),
          axios.get(`${API}/dashboard/top-products?limit=4`, { withCredentials: true }),
          axios.get(`${API}/dashboard/sales-by-category`, { withCredentials: true }),
          axios.get(`${API}/sales/cancelled?limit=200&include_presales=true`, { withCredentials: true }),
          axios.get(`${API}/accounts/receivables/overdue/summary`, { withCredentials: true }).catch(() => ({ data: { count: 0, total: 0, total_with_interest: 0 } })),
          axios.get(`${API}/cashflow/daily?year=${yyyy}&month=${mm}`, { withCredentials: true }).catch(() => ({ data: { totals: { profit: 0, revenue: 0 } } })),
          axios.get(`${API}/sales/discount-analytics`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/inventory/alerts?stale_days=60`, { withCredentials: true }).catch(() => ({ data: { low_stock_count: 0, stale_count: 0, stale: [], overstock: [] } })),
          axios.get(`${API}/sales?start_date=${yStart}&end_date=${yEnd}&limit=1000`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);

        const todayTotal = (salesRes.data || []).reduce((s, x) => s + (Number(x.total) || 0), 0);
        setTodayStats({ count: (salesRes.data || []).length, total: todayTotal });
        const yTotal = (ySalesRes.data || []).reduce((s, x) => s + (Number(x.total) || 0), 0);
        setYesterdayStats({ count: (ySalesRes.data || []).length, total: yTotal });

        setComparison(compRes.data);
        setTopProducts(topRes.data || []);
        setSalesByCategory(catRes.data);
        setOverdue(overdueRes.data);
        setMonthProfit(Number(dailyRes.data?.totals?.profit || 0));
        setMonthRevenue(Number(dailyRes.data?.totals?.revenue || 0));
        setDiscountAnalytics(discRes.data || null);

        // Estornos de hoje
        const today = new Date();
        const isToday = (iso) => {
          if (!iso) return false;
          const d = new Date(iso);
          return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
        };
        const cancList = (cancelledRes.data || []).filter(s => isToday(s.cancelled_at));
        setTodayReversals({
          count: cancList.length,
          total: cancList.reduce((s, x) => s + (Number(x.total) || 0), 0),
        });

        // Estoque + capital parado
        const inv = invRes.data || {};
        const staleList = inv.stale || [];
        const overstockList = inv.overstock || [];
        const capital = Number(inv.capital_locked_total) || 0;
        setInventory({
          low_stock_count: inv.low_stock_count || 0,
          stale_count: inv.stale_count || 0,
          capital_locked: capital,
          capital_top: inv.capital_top || [],
          stale: staleList,
          overstock: overstockList,
        });
      } catch (e) {
        console.error('dashboard load error', e);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [now]);

  // Dados finais (com simulação se ativada)
  const data = useMemo(() => {
    if (simulation) {
      const S = SIMULATION_DATA;
      const growth = ((S.todaySales - S.yesterdaySales) / S.yesterdaySales) * 100;
      const marginPct = (S.monthProfit / S.monthRevenue) * 100;
      const goalProgress = (S.yearSales / S.yearGoal) * 100;
      return {
        todaySales: S.todaySales,
        todayCount: S.todayCount,
        yesterdaySales: S.yesterdaySales,
        growth,
        monthSales: S.monthSales,
        yearSales: S.yearSales,
        yearGoal: S.yearGoal,
        goalProgress,
        monthProfit: S.monthProfit,
        marginPct,
        discountTimeline: S.discountTimeline,
        categoryPie: S.categoryPie,
        topProducts: S.topProducts,
        lowStockCount: S.lowStockCount,
        staleCount: S.staleCount,
        capitalLocked: S.capitalLocked,
        reversalsCount: S.reversalsCount,
        reversalsTotal: S.reversalsTotal,
        overdueCount: S.overdueCount,
        overdueTotal: S.overdueTotal,
      };
    }
    // Dados reais
    const currentMonthIdx = now.getMonth();
    const monthSales = Number(comparison?.months?.[currentMonthIdx]?.current_year_total || 0);
    const yearSales = Number(comparison?.totals?.current_year || 0);
    const prevYearAnnual = Number(comparison?.totals?.previous_year || 0);
    const yearGoal = prevYearAnnual > 0 ? prevYearAnnual * 1.2 : 500000;
    const goalProgress = Math.min(100, (yearSales / yearGoal) * 100);
    const growth = yesterdayStats.total > 0
      ? ((todayStats.total - yesterdayStats.total) / yesterdayStats.total) * 100
      : (todayStats.total > 0 ? 100 : 0);
    const marginPct = monthRevenue > 0 ? (monthProfit / monthRevenue) * 100 : 0;
    const categoryPie = (salesByCategory?.categories || []).map((c) => ({
      name: CATEGORY_LABELS[c.category] || c.category,
      value: c.total,
    }));
    const discountTimeline = discountAnalytics?.timeline || [];
    return {
      todaySales: todayStats.total,
      todayCount: todayStats.count,
      yesterdaySales: yesterdayStats.total,
      growth,
      monthSales,
      yearSales,
      yearGoal,
      goalProgress,
      monthProfit,
      marginPct,
      discountTimeline,
      categoryPie,
      topProducts,
      lowStockCount: inventory.low_stock_count,
      staleCount: inventory.stale_count,
      capitalLocked: inventory.capital_locked,
      reversalsCount: todayReversals.count,
      reversalsTotal: todayReversals.total,
      overdueCount: overdue.count,
      overdueTotal: overdue.total_with_interest || overdue.total,
    };
  }, [simulation, now, comparison, todayStats, yesterdayStats, monthProfit, monthRevenue, salesByCategory, discountAnalytics, topProducts, inventory, todayReversals, overdue]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full" style={{ background: COLORS.bg }}>
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`min-h-full ${isLight ? 'dashboard-light' : ''}`} style={{ background: COLORS.bg }} data-testid="dashboard-page">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] font-bold text-emerald-400">Smart Flow · Performance</p>
              <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mt-1">Dashboard</h1>
              <p className="text-sm text-slate-400 mt-1">Bem-vindo, <span className="text-slate-200 font-medium">{user?.name}</span> · {now.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p>
            </div>
            <button
              onClick={() => setSimulation(!simulation)}
              className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-all border ${
                simulation
                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/50 hover:bg-amber-500/30'
                  : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700 hover:text-slate-200'
              }`}
              data-testid="simulation-toggle"
            >
              <Sparkles size={14} className="inline mr-2" />
              Modo Simulação {simulation ? '✓' : 'off'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <KPICard
              label="Vendas Hoje"
              value={fmtBRL(data.todaySales)}
              secondary={`${data.todayCount} vendas`}
              icon={<Zap size={20} />}
              accent={COLORS.emerald}
              testId="kpi-today"
              colors={COLORS}
              isLight={isLight}
              indicator={
                <div className="flex items-center gap-1 text-sm font-bold text-white">
                  {data.growth >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {data.growth >= 0 ? '+' : ''}{data.growth.toFixed(1)}% vs ontem
                </div>
              }
            />
            <KPICard
              label="Mês Atual"
              value={fmtBRL(data.monthSales)}
              secondary={MONTHS_PT[now.getMonth()] + '/' + now.getFullYear()}
              icon={<DollarSign size={20} />}
              accent={COLORS.cyan}
              testId="kpi-month"
              colors={COLORS}
              isLight={isLight}
            />
            <KPICard
              label="Faturamento Anual"
              value={fmtBRL(data.yearSales)}
              secondary={`Meta ${fmtBRL(data.yearGoal)}`}
              icon={<Target size={20} />}
              accent={COLORS.amber}
              testId="kpi-year"
              colors={COLORS}
              isLight={isLight}
              footer={
                <div className="mt-2">
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${Math.min(100, data.goalProgress)}%`,
                        background: `linear-gradient(90deg, ${COLORS.emerald}, ${COLORS.amber})`,
                      }}
                      data-testid="year-goal-bar"
                    />
                  </div>
                  <p className="text-sm text-white font-bold mt-1 font-mono">{data.goalProgress.toFixed(1)}% da meta</p>
                </div>
              }
            />
            <KPICard
              label="Lucro Líquido Estimado"
              value={fmtBRL(data.monthProfit)}
              secondary={`Margem ${data.marginPct.toFixed(1)}%`}
              icon={<Award size={20} />}
              accent={data.marginPct >= 15 ? COLORS.emerald : data.marginPct >= 8 ? COLORS.amber : COLORS.coral}
              testId="kpi-profit"
              colors={COLORS}
              isLight={isLight}
              footer={
                data.marginPct >= 15 ? (
                  <span className="mt-2 inline-block text-xs uppercase tracking-widest font-black text-white bg-white/10 border border-white/30 px-2 py-0.5 rounded" data-testid="healthy-margin-badge">✓ Margem Saudável</span>
                ) : data.marginPct >= 8 ? (
                  <span className="mt-2 inline-block text-xs uppercase tracking-widest font-black text-white bg-white/10 border border-white/30 px-2 py-0.5 rounded">Margem regular</span>
                ) : (
                  <span className="mt-2 inline-block text-xs uppercase tracking-widest font-black text-white bg-white/10 border border-white/30 px-2 py-0.5 rounded">⚠ Margem baixa</span>
                )
              }
            />
          </div>

          {/* Gráfico Mensal (Mês a Mês / Ano a Ano) - TOPO */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
            <div className="lg:col-span-3 rounded-md p-5" style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }} data-testid="chart-monthly">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Evolução Financeira</p>
                  <h3 className="text-base font-bold text-white mt-0.5">Vendas Mês a Mês · Ano a Ano</h3>
                </div>
                <div className="p-2 rounded" style={{ background: 'rgba(16, 185, 129, 0.15)' }}>
                  <BarChart3 size={18} className="text-emerald-400" />
                </div>
              </div>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={(comparison?.months || []).map((m, i) => ({ label: MONTHS_PT[i], atual: m.current_year_total, anterior: m.previous_year_total }))} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke={COLORS.border} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: COLORS.slate }} />
                    <YAxis tick={{ fontSize: 10, fill: COLORS.slate }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} width={45} />
                    <Tooltip contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 11, color: '#fff' }} labelStyle={{ color: COLORS.slateLight, fontWeight: 700 }} formatter={(v) => fmtBRL(v)} />
                    <Legend wrapperStyle={{ fontSize: 11, color: COLORS.slate }} iconSize={8} />
                    <Line type="monotone" dataKey="atual" name={`${now.getFullYear()}`} stroke={COLORS.emerald} strokeWidth={3} dot={{ r: 4, fill: COLORS.emerald }} activeDot={{ r: 6 }} />
                    <Line type="monotone" dataKey="anterior" name={`${now.getFullYear() - 1}`} stroke={COLORS.slate} strokeWidth={2} dot={{ r: 3 }} strokeDasharray="6 4" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Pizza de categorias */}
            <div className="lg:col-span-2 rounded-md p-5" style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }} data-testid="chart-category">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Vendas por Categoria</p>
                  <h3 className="text-base font-bold text-white mt-0.5">Distribuição</h3>
                </div>
                <div className="p-2 rounded" style={{ background: 'rgba(34, 211, 238, 0.15)' }}>
                  <Layers size={18} className="text-cyan-400" />
                </div>
              </div>
              {data.categoryPie.length === 0 ? (
                <p className="text-sm text-slate-500 py-16 text-center">Sem dados de vendas por categoria.</p>
              ) : (() => {
                const totalPie = data.categoryPie.reduce((s, x) => s + (x.value || 0), 0);
                const withPct = data.categoryPie.map((c) => ({
                  ...c,
                  pct: totalPie > 0 ? (c.value / totalPie) * 100 : 0,
                })).sort((a, b) => b.value - a.value);
                // Label externo: porcentagem em branco
                const renderLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
                  const RAD = Math.PI / 180;
                  const r = outerRadius + 16;
                  const x = cx + r * Math.cos(-midAngle * RAD);
                  const y = cy + r * Math.sin(-midAngle * RAD);
                  if (percent < 0.04) return null;
                  return (
                    <text
                      x={x}
                      y={y}
                      fill={isLight ? '#0F172A' : '#FFFFFF'}
                      textAnchor={x > cx ? 'start' : 'end'}
                      dominantBaseline="central"
                      fontSize={13}
                      fontWeight={800}
                    >
                      {`${(percent * 100).toFixed(1)}%`}
                    </text>
                  );
                };
                return (
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 items-center">
                    <div className="lg:col-span-3 relative" style={{ width: '100%', height: 280 }}>
                      {/* Total no centro do donut */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ zIndex: 2 }}>
                        <span className="text-[9px] uppercase tracking-widest font-bold" style={{ color: isLight ? '#64748B' : '#94A3B8' }}>Total</span>
                        <span className="text-xl font-black font-mono" style={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>{fmtBRL(totalPie)}</span>
                        <span className="text-[10px] font-semibold" style={{ color: isLight ? '#475569' : '#CBD5E1' }}>{withPct.length} categorias</span>
                      </div>
                      <ResponsiveContainer>
                        <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                          <defs>
                            <filter id="pieShadow" x="-20%" y="-20%" width="140%" height="140%">
                              <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.35" />
                            </filter>
                          </defs>
                          <Pie
                            data={withPct}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={100}
                            paddingAngle={3}
                            labelLine={{ stroke: isLight ? '#64748B' : '#94A3B8', strokeWidth: 1 }}
                            label={renderLabel}
                            isAnimationActive
                            filter="url(#pieShadow)"
                          >
                            {withPct.map((entry, i) => (
                              <Cell
                                key={i}
                                fill={colorForCategory(entry.name, i)}
                                stroke={COLORS.bg}
                                strokeWidth={3}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 12, color: '#fff', padding: '8px 12px' }}
                            labelStyle={{ color: '#F1F5F9', fontWeight: 800, marginBottom: 4 }}
                            itemStyle={{ color: '#F1F5F9' }}
                            formatter={(v, n, p) => [`${fmtBRL(v)} · ${(p.payload.pct || 0).toFixed(1)}%`, n]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Legenda customizada com barra proporcional */}
                    <div className="lg:col-span-2 space-y-2" data-testid="category-legend">
                      {withPct.map((c, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`cat-legend-${i}`}>
                          <span
                            className="shrink-0 w-3 h-3 rounded-sm"
                            style={{ background: colorForCategory(c.name, i), boxShadow: `0 0 0 2px ${colorForCategory(c.name, i)}30` }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-sm font-bold text-white truncate">{c.name}</span>
                              <span className="text-sm font-black text-white font-mono">{c.pct.toFixed(1)}%</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden">
                                <div
                                  className="h-full"
                                  style={{ width: `${c.pct}%`, background: colorForCategory(c.name, i) }}
                                />
                              </div>
                              <span className="text-[10px] text-slate-400 font-mono shrink-0">{fmtBRL(c.value)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Evolução Diária (Descontos) - abaixo do mensal */}
          <div className="grid grid-cols-1 gap-4 mb-6">
            <div className="rounded-md p-5" style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }} data-testid="chart-discount">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Evolução Diária</p>
                  <h3 className="text-base font-bold text-white mt-0.5">Análise de Descontos por Dia</h3>
                </div>
                <div className="p-2 rounded" style={{ background: 'rgba(245, 158, 11, 0.15)' }}>
                  <Percent size={18} className="text-amber-400" />
                </div>
              </div>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <LineChart data={data.discountTimeline} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke={COLORS.border} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: COLORS.slate }} />
                    <YAxis tick={{ fontSize: 10, fill: COLORS.slate }} tickFormatter={(v) => `${Number(v).toFixed(0)}`} width={40} />
                    <Tooltip
                      contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 11 }}
                      labelStyle={{ color: COLORS.slateLight, fontWeight: 600 }}
                      formatter={(v, n) => n === 'discount' ? `R$ ${Number(v).toFixed(2)}` : `${Number(v).toFixed(2)}%`}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: COLORS.slate }} iconSize={8} />
                    <Line type="monotone" dataKey="discount" name="Desconto (R$)" stroke={COLORS.amber} strokeWidth={2.5} dot={{ r: 3, fill: COLORS.amber }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="percent" name="% s/ Faturamento" stroke={COLORS.violet} strokeWidth={2} dot={{ r: 2 }} strokeDasharray="4 4" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top Delivery */}
          <div className="mb-6" data-testid="top-delivery-wrap">
            <TopDeliveryCustomers />
          </div>

          {/* Inteligência de Estoque: 3 blocos coloridos - MOVIDOS PARA BAIXO */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <IntelBlock
              label="Estoque Baixo"
              value={data.lowStockCount}
              suffix="produtos"
              hint="Precisam reposição urgente"
              color={COLORS.blue}
              solid
              icon={<AlertCircle size={18} />}
              testId="intel-low-stock"
            />
            <IntelBlock
              label="Sem Vendas há 60 dias"
              value={data.staleCount}
              suffix="produtos parados"
              hint="Considere promoção ou descontinuar"
              color={COLORS.purple}
              solid
              icon={<Package size={18} />}
              testId="intel-stale"
            />
            <IntelBlock
              label="Capital Parado no Estoque"
              value={fmtBRL(data.capitalLocked)}
              suffix=""
              hint="Valor imobilizado em produtos parados"
              color={COLORS.brown}
              solid
              icon={<DollarSign size={18} />}
              isMoney
              testId="intel-capital-locked"
            />
          </div>

          {!simulation && (inventory.capital_top || []).length > 0 && (
            <div className="mb-6 flex justify-end">
              <button
                onClick={() => setShowCapitalDiag(true)}
                className="text-xs font-bold text-blue-600 hover:text-blue-800 underline"
                data-testid="open-capital-diag-btn"
              >
                Diagnóstico do capital parado (top 10 por valor)
              </button>
            </div>
          )}

          {/* Auditoria + Top Produtos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Auditoria */}
            <div className="rounded-md p-5" style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }} data-testid="audit-box">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Auditoria</p>
                  <h3 className="text-lg font-bold text-white mt-0.5">Estornos & Crediário Vencido</h3>
                </div>
                <div className="p-2 rounded" style={{ background: 'rgba(248, 113, 113, 0.15)' }}>
                  <AlertTriangle size={18} className="text-red-400" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <AuditRow
                  icon={<RotateCcw size={16} className="text-amber-400" />}
                  label="Estornos hoje"
                  value={fmtBRL(data.reversalsTotal)}
                  count={data.reversalsCount}
                  testId="audit-reversals"
                  colors={COLORS}
                  isLight={isLight}
                />
                <AuditRow
                  icon={<AlertTriangle size={16} className="text-red-400" />}
                  label="Crediário vencido"
                  value={fmtBRL(data.overdueTotal)}
                  count={data.overdueCount}
                  testId="audit-overdue"
                  colors={COLORS}
                  isLight={isLight}
                />
              </div>
            </div>

            {/* Top 4 Produtos */}
            <div className="rounded-md p-5" style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }} data-testid="top-products-box">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Ranking do Ano</p>
                  <h3 className="text-lg font-bold text-white mt-0.5">Top 4 Produtos Mais Vendidos</h3>
                </div>
                <div className="p-2 rounded" style={{ background: 'rgba(16, 185, 129, 0.15)' }}>
                  <ShoppingBag size={18} className="text-emerald-400" />
                </div>
              </div>
              {data.topProducts.length === 0 ? (
                <p className="text-xs text-slate-500 py-6 text-center">Nenhuma venda registrada ainda.</p>
              ) : (
                <ol className="space-y-1.5" data-testid="top-products-list">
                  {data.topProducts.slice(0, 4).map((p, i) => (
                    <li key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-700/40 last:border-0">
                      <span className={`shrink-0 w-6 h-6 rounded flex items-center justify-center text-[10px] font-black ${
                        i === 0 ? 'bg-amber-400 text-slate-900' :
                        i === 1 ? 'bg-slate-300 text-slate-900' :
                        i === 2 ? 'bg-orange-400 text-slate-900' :
                        'bg-slate-700 text-slate-300'
                      }`}>#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-100 truncate">{p.name || p.product_name}</p>
                        <p className="text-[10px] text-slate-500">{fmtInt(p.total_quantity)} unidades</p>
                      </div>
                      <span className="text-sm font-bold text-emerald-400 font-mono">{fmtBRL(p.total_revenue || p.revenue || 0)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* Fiado: Dívidas Vencidas (+30 dias) + Top 5 Devedores */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-testid="fiado-widgets-wrap">
            <OverdueDebtsWidget colors={COLORS} isLight={isLight} onOpenStatement={setStatementCustomerId} />
            <TopDebtorsWidget colors={COLORS} isLight={isLight} onOpenStatement={setStatementCustomerId} />
          </div>

          {/* Despesas Fixas: Vencimentos próximos */}
          <div className="mb-6" data-testid="expense-due-widget-wrap">
            <ExpenseDueAlertsWidget colors={COLORS} isLight={isLight} />
          </div>

          {/* Alertas Pendentes — movido para o final, após Estornos e Crediários Vencidos */}
          <div className="mb-6" data-testid="dashboard-alerts-wrap">
            <DashboardAlerts />
          </div>

          {/* Footer simulation notice */}
          {simulation && (
            <div className="rounded-md p-3 mb-6 text-center text-xs border" style={{ background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.4)' }}>
              <Sparkles size={12} className="inline mr-1 text-amber-400" />
              <span className="text-amber-300 font-bold uppercase tracking-wider">Modo Simulação ativo</span>
              <span className="text-slate-400 ml-2">— dados fictícios para apresentação. Clique novamente para voltar aos dados reais.</span>
            </div>
          )}
        </div>
      </div>
      {statementCustomerId && (
        <CustomerStatementModal customerId={statementCustomerId} onClose={() => setStatementCustomerId(null)} />
      )}
      {showCapitalDiag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" {...overlayClose(() => setShowCapitalDiag(false))} data-testid="capital-diag-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Diagnóstico do Capital Parado</h3>
                <p className="text-sm text-slate-500">Top 10 produtos por valor (estoque × custo). Verifique se algum tem custo digitado errado.</p>
              </div>
              <button onClick={() => setShowCapitalDiag(false)} className="text-slate-400 hover:text-slate-700 text-xl font-bold" data-testid="close-capital-diag-btn">×</button>
            </div>
            <div className="p-5 overflow-y-auto">
              <table className="w-full border border-slate-200 rounded">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">Produto</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase">Estoque</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase">Custo</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(inventory.capital_top || []).map((p, i) => {
                    const suspeito = Number(p.cost_price) >= 100000;
                    return (
                      <tr key={p.id || i} className={suspeito ? 'bg-red-50' : ''} data-testid={`capital-diag-row-${i}`}>
                        <td className="px-3 py-2 text-sm">
                          <span className="font-medium">{p.name}</span>
                          {p.code && <span className="text-xs text-slate-400 ml-2 font-mono">{p.code}</span>}
                          {suspeito && <span className="ml-2 text-[10px] font-bold text-red-600">CUSTO SUSPEITO</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-sm">{Number(p.stock_quantity).toLocaleString('pt-BR')}</td>
                        <td className={`px-3 py-2 text-right text-sm ${suspeito ? 'text-red-600 font-bold' : ''}`}>R$ {Number(p.cost_price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 text-right text-sm font-bold">R$ {Number(p.subtotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-xs text-slate-400 mt-3">Produtos com custo ≥ R$ 100.000 (destacados) são ignorados no total do capital parado. Corrija o custo do produto para normalizar.</p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

/* ---------- Subcomponentes ---------- */
const KPICard = ({ label, value, secondary, icon, accent, indicator, footer, testId, colors, isLight }) => (
  <div className="rounded-md p-5 relative overflow-hidden group transition-all hover:-translate-y-0.5" style={{ background: colors.card, border: `1px solid ${colors.border}`, boxShadow: isLight ? '0 1px 3px rgba(15, 23, 42, 0.06)' : 'none' }} data-testid={testId}>
    <div className="absolute top-0 right-0 w-24 h-24 opacity-10 rounded-full blur-2xl" style={{ background: accent }} />
    <div className="flex items-start justify-between mb-2 relative">
      <p className="text-xs uppercase tracking-[0.25em] font-black" style={{ color: colors.textStrong }}>{label}</p>
      <div className="p-2 rounded" style={{ background: `${accent}25`, color: accent }}>{icon}</div>
    </div>
    <p className="font-black mt-1 font-mono tracking-tight" style={{ fontSize: '36px', lineHeight: 1.1, color: colors.textStrong }} data-testid={`${testId}-value`}>{value}</p>
    {secondary && <p className="text-sm font-bold mt-1" style={{ color: colors.textStrong }}>{secondary}</p>}
    {indicator && <div className="mt-2">{indicator}</div>}
    {footer}
  </div>
);

const IntelBlock = ({ label, value, suffix, hint, color, icon, isMoney, solid, testId }) => (
  // Blocos SEMPRE solid com fundo colorido — mesma aparência em tema claro e escuro
  <div
    className="rounded-md p-5 relative overflow-hidden"
    style={{
      background: color,
      border: `1px solid ${color}`,
      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
    }}
    data-testid={testId}
  >
    <div className="flex items-start justify-between mb-2">
      <p className="text-sm uppercase tracking-[0.2em] font-black text-white">{label}</p>
      <div className="p-1.5 rounded" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>{icon}</div>
    </div>
    <p
      className="font-black mt-1 tracking-tight text-white"
      style={{ fontSize: isMoney ? '32px' : '44px', lineHeight: 1.05, fontFamily: isMoney ? 'monospace' : undefined }}
      data-testid={`${testId}-value`}
    >
      {value}{suffix && !isMoney && <span className="text-lg font-bold ml-2 text-white">{suffix}</span>}
    </p>
    <p className="text-sm mt-2 font-semibold text-white/90">{hint}</p>
  </div>
);

const AuditRow = ({ icon, label, value, count, testId, colors, isLight }) => (
  <div className="p-3 rounded border" style={{ background: isLight ? colors.chipBg : 'rgba(15, 23, 42, 0.5)', borderColor: colors.border }} data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: colors.textMuted }}>{label}</p>
    </div>
    <p className="text-lg font-black font-mono" style={{ color: colors.textStrong }}>{value}</p>
    <p className="text-[10px]" style={{ color: colors.textMuted }}>{count} ocorrência{count !== 1 ? 's' : ''}</p>
  </div>
);

export default DashboardPage;
